import React, { useState } from 'react';
import { Plus, Trash2, Building2 } from 'lucide-react';

interface BlockData {
  block_name: string;
  block_order: number;
  total_floors: number;
  elevators_count: number;
  studio_count: number;
  f2_count: number;
  f2_duplex_count?: number;
  f2_triplex_count?: number;
  f3_count: number;
  f3_duplex_count?: number;
  f3_triplex_count?: number;
  f4_count: number;
  f4_duplex_count?: number;
  f4_triplex_count?: number;
  f5_count: number;
  f5_duplex_count?: number;
  f5_triplex_count?: number;
  commercial_count: number;
  duplex_count?: number;
  triplex_count?: number;
}

interface Step2Props {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const BLOCK_COLORS = [
  { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: 'text-blue-600' },
  { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', icon: 'text-green-600' },
  { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', icon: 'text-orange-600' },
  { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', icon: 'text-purple-600' },
  { bg: 'bg-pink-50 dark:bg-pink-900/20', border: 'border-pink-200 dark:border-pink-800', icon: 'text-pink-600' },
  { bg: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-200 dark:border-teal-800', icon: 'text-teal-600' },
  { bg: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-200 dark:border-cyan-800', icon: 'text-cyan-600' },
  { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: 'text-amber-600' },
];

export const Step2ConfigBlocs: React.FC<Step2Props> = ({
  data,
  onUpdate,
  onNext,
  onBack,
}) => {
  const [blocks, setBlocks] = useState<BlockData[]>(
    data.blocks.length > 0
      ? data.blocks
      : [
          {
            block_name: 'Bloc A',
            block_order: 1,
            total_floors: 5,
            elevators_count: 1,
            studio_count: 0,
            f2_count: 0,
            f2_duplex_count: 0,
            f2_triplex_count: 0,
            f3_count: 0,
            f3_duplex_count: 0,
            f3_triplex_count: 0,
            f4_count: 0,
            f4_duplex_count: 0,
            f4_triplex_count: 0,
            f5_count: 0,
            f5_duplex_count: 0,
            f5_triplex_count: 0,
            commercial_count: 0,
          },
        ]
  );
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  const addBlock = () => {
    const newBlock: BlockData = {
      block_name: `Bloc ${String.fromCharCode(65 + blocks.length)}`,
      block_order: blocks.length + 1,
      total_floors: 5,
      elevators_count: 1,
      studio_count: 0,
      f2_count: 0,
      f2_duplex_count: 0,
      f2_triplex_count: 0,
      f3_count: 0,
      f3_duplex_count: 0,
      f3_triplex_count: 0,
      f4_count: 0,
      f4_duplex_count: 0,
      f4_triplex_count: 0,
      f5_count: 0,
      f5_duplex_count: 0,
      f5_triplex_count: 0,
      commercial_count: 0,
    };
    setBlocks([...blocks, newBlock]);
  };

  const removeBlock = (index: number) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    newBlocks.forEach((block, idx) => {
      block.block_order = idx + 1;
    });
    setBlocks(newBlocks);
  };

  const updateBlock = (index: number, field: keyof BlockData, value: any) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], [field]: value };
    setBlocks(newBlocks);

    const newErrors = { ...errors };
    if (newErrors[index]) {
      delete newErrors[index][field];
    }
    setErrors(newErrors);
  };

  const calculateNormalCount = (block: BlockData, type: 'f2' | 'f3' | 'f4' | 'f5') => {
    const total = block[`${type}_count`] || 0;
    const duplex = block[`${type}_duplex_count`] || 0;
    const triplex = block[`${type}_triplex_count`] || 0;
    return total - duplex - triplex;
  };

  const getTotalUnits = (block: BlockData) => {
    return (
      block.studio_count +
      block.f2_count +
      block.f3_count +
      block.f4_count +
      block.f5_count +
      block.commercial_count
    );
  };

  const getTotalDuplexTriplex = (block: BlockData) => {
    const totalDuplex =
      (block.f2_duplex_count || 0) +
      (block.f3_duplex_count || 0) +
      (block.f4_duplex_count || 0) +
      (block.f5_duplex_count || 0);

    const totalTriplex =
      (block.f2_triplex_count || 0) +
      (block.f3_triplex_count || 0) +
      (block.f4_triplex_count || 0) +
      (block.f5_triplex_count || 0);

    return { totalDuplex, totalTriplex };
  };

  const validate = () => {
    const newErrors: Record<number, Record<string, string>> = {};

    blocks.forEach((block, index) => {
      const blockErrors: Record<string, string> = {};

      if (!block.block_name.trim()) {
        blockErrors.block_name = 'Le nom du bloc est obligatoire';
      }
      if (block.total_floors < 1) {
        blockErrors.total_floors = 'Au moins 1 étage requis';
      }
      if (getTotalUnits(block) === 0) {
        blockErrors.units = 'Au moins un type de logement requis';
      }

      ['f2', 'f3', 'f4', 'f5'].forEach((type) => {
        const t = type as 'f2' | 'f3' | 'f4' | 'f5';
        const total = block[`${t}_count`] || 0;
        const duplex = block[`${t}_duplex_count`] || 0;
        const triplex = block[`${t}_triplex_count`] || 0;

        if (duplex + triplex > total) {
          blockErrors[`${type}_variants`] = `Erreur ${type.toUpperCase()}: ${duplex} duplex + ${triplex} triplex > ${total} total`;
        }
      });

      if (Object.keys(blockErrors).length > 0) {
        newErrors[index] = blockErrors;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      const blocksToSave = blocks.map((block) => {
        const { totalDuplex, totalTriplex } = getTotalDuplexTriplex(block);
        return {
          ...block,
          duplex_count: totalDuplex,
          triplex_count: totalTriplex,
        };
      });

      onUpdate({ blocks: blocksToSave });
      onNext();
    }
  };

  const renderUnitTypeField = (
    block: BlockData,
    blockIndex: number,
    type: 'studio' | 'f2' | 'f3' | 'f4' | 'f5' | 'commercial',
    label: string
  ) => {
    const canHaveDuplexTriplex = type !== 'studio' && type !== 'commercial';
    const count = block[`${type}_count`] || 0;
    const duplexCount = canHaveDuplexTriplex ? block[`${type}_duplex_count` as keyof BlockData] || 0 : 0;
    const triplexCount = canHaveDuplexTriplex ? block[`${type}_triplex_count` as keyof BlockData] || 0 : 0;
    const normalCount = canHaveDuplexTriplex ? calculateNormalCount(block, type as 'f2' | 'f3' | 'f4' | 'f5') : count;

    const isInvalid = canHaveDuplexTriplex && ((duplexCount as number) + (triplexCount as number)) > count;

    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </label>
          <input
            type="number"
            min="0"
            value={count}
            onChange={(e) => {
              const newValue = parseInt(e.target.value) || 0;
              updateBlock(blockIndex, `${type}_count` as keyof BlockData, newValue);

              if (canHaveDuplexTriplex) {
                const duplex = block[`${type}_duplex_count` as keyof BlockData] || 0;
                const triplex = block[`${type}_triplex_count` as keyof BlockData] || 0;
                if (duplex + triplex > newValue) {
                  updateBlock(blockIndex, `${type}_duplex_count` as keyof BlockData, 0);
                  updateBlock(blockIndex, `${type}_triplex_count` as keyof BlockData, 0);
                }
              }
            }}
            className="w-20 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-center"
          />
        </div>

        {canHaveDuplexTriplex && count > 0 && (
          <div className="ml-4 space-y-3 pt-2 border-t border-gray-200 dark:border-gray-600">
            <div className="space-y-2">
              <label className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={(duplexCount as number) > 0}
                  onChange={(e) => {
                    updateBlock(
                      blockIndex,
                      `${type}_duplex_count` as keyof BlockData,
                      e.target.checked ? 1 : 0
                    );
                  }}
                  className="mr-2 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                Inclure des duplex
              </label>

              {(duplexCount as number) > 0 && (
                <div className="ml-6 flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max={count}
                    value={duplexCount}
                    onChange={(e) => {
                      const newValue = Math.min(parseInt(e.target.value) || 0, count);
                      updateBlock(blockIndex, `${type}_duplex_count` as keyof BlockData, newValue);
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-center text-sm"
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {label} duplex (2 étages)
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={(triplexCount as number) > 0}
                  onChange={(e) => {
                    updateBlock(
                      blockIndex,
                      `${type}_triplex_count` as keyof BlockData,
                      e.target.checked ? 1 : 0
                    );
                  }}
                  className="mr-2 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                Inclure des triplex
              </label>

              {(triplexCount as number) > 0 && (
                <div className="ml-6 flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max={count}
                    value={triplexCount}
                    onChange={(e) => {
                      const newValue = Math.min(parseInt(e.target.value) || 0, count);
                      updateBlock(blockIndex, `${type}_triplex_count` as keyof BlockData, newValue);
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white text-center text-sm"
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {label} triplex (3 étages)
                  </span>
                </div>
              )}
            </div>

            {((duplexCount as number) > 0 || (triplexCount as number) > 0) && (
              <div
                className={`text-xs p-2 rounded ${
                  isInvalid
                    ? 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-600'
                    : 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                }`}
              >
                {isInvalid ? '⚠️' : '💡'} Soit : {normalCount} {label} normaux
                {(duplexCount as number) > 0 && ` + ${duplexCount} duplex`}
                {(triplexCount as number) > 0 && ` + ${triplexCount} triplex`}
              </div>
            )}

            {isInvalid && (
              <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                Erreur : {duplexCount} duplex + {triplexCount} triplex ne peut pas dépasser {count} {label} au total
              </div>
            )}
          </div>
        )}

        {errors[blockIndex]?.[`${type}_variants`] && (
          <p className="text-xs text-red-600 mt-1">{errors[blockIndex][`${type}_variants`]}</p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Configuration des blocs
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Définissez les blocs et la répartition des logements
        </p>
      </div>

      <div className="space-y-6">
        {blocks.map((block, index) => {
          const { totalDuplex, totalTriplex } = getTotalDuplexTriplex(block);
          const colorScheme = BLOCK_COLORS[index % BLOCK_COLORS.length];

          return (
            <div
              key={index}
              className={`border ${colorScheme.border} rounded-lg p-6 ${colorScheme.bg}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Building2 className={`w-5 h-5 ${colorScheme.icon} mr-2`} />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {block.block_name}
                  </h3>
                </div>
                {blocks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeBlock(index)}
                    className="text-red-600 hover:text-red-700 p-2"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nom du bloc *
                  </label>
                  <input
                    type="text"
                    value={block.block_name}
                    onChange={(e) => updateBlock(index, 'block_name', e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white ${
                      errors[index]?.block_name
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Bloc A"
                  />
                  {errors[index]?.block_name && (
                    <p className="mt-1 text-sm text-red-600">{errors[index].block_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nombre d'étages *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={block.total_floors}
                    onChange={(e) => updateBlock(index, 'total_floors', parseInt(e.target.value) || 0)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white ${
                      errors[index]?.total_floors
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors[index]?.total_floors && (
                    <p className="mt-1 text-sm text-red-600">{errors[index].total_floors}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nombre d'ascenseurs
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={block.elevators_count}
                    onChange={(e) => updateBlock(index, 'elevators_count', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Répartition des logements *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderUnitTypeField(block, index, 'studio', 'Studio')}
                  {renderUnitTypeField(block, index, 'f2', 'F2')}
                  {renderUnitTypeField(block, index, 'f3', 'F3')}
                  {renderUnitTypeField(block, index, 'f4', 'F4')}
                  {renderUnitTypeField(block, index, 'f5', 'F5')}
                  {renderUnitTypeField(block, index, 'commercial', 'Commercial')}
                </div>
                {errors[index]?.units && (
                  <p className="mt-2 text-sm text-red-600">{errors[index].units}</p>
                )}
              </div>

              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg space-y-1">
                <p className="text-sm text-green-800 dark:text-green-300">
                  <span className="font-semibold">Total:</span> {getTotalUnits(block)} logements
                </p>
                {(totalDuplex > 0 || totalTriplex > 0) && (
                  <p className="text-xs text-green-700 dark:text-green-400">
                    → dont {totalDuplex} duplex et {totalTriplex} triplex
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addBlock}
        className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-green-500 hover:text-green-600 dark:hover:text-green-400 flex items-center justify-center transition-colors"
      >
        <Plus className="w-5 h-5 mr-2" />
        Ajouter un bloc
      </button>

      <div className="flex justify-between pt-6 border-t dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          ← Retour
        </button>

        <button
          type="submit"
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Suivant →
        </button>
      </div>
    </form>
  );
};
